// src/components/work/WorkDetailsDrawer.jsx
// Work Drawer — Overview only (auto-save, premium UI)
// - Email tab removed
// - Status change auto-saves immediately
// - Assignees & Schedule auto-save (debounced); no buttons
// - Sticky header, solid red Delete + kebab, dock-aware padding
// - UI polish: aligned Status/Assignees, chips below input, 44px controls, improved typography

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  useWork,
  useUpdateWork,
  useTransitionWork,
  useDeleteWork,
} from '../../hooks/useWorkQueries';

import { useUsers, useUserSearch } from '../../hooks/useUserQueries';
import { MIN_SEARCH_CHARS } from '../../services/userService';

/* ---------------------------------- utils --------------------------------- */
const cx = (...args) =>
  args
    .flatMap((a) =>
      typeof a === 'string'
        ? a
        : a && typeof a === 'object'
        ? Object.keys(a).filter((k) => a[k])
        : []
    )
    .join(' ')
    .trim();

const TYPE_LABEL = { task: 'Task', job: 'Job', appointment: 'Appointment' };

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const humanizeDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

/* -------------------------- tiny debounced effect -------------------------- */
function useDebouncedEffect(effect, deps, delay = 600) {
  useEffect(() => {
    const h = setTimeout(() => {
      effect();
    }, delay);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, delay]);
}

/* --------------------------------- icons ---------------------------------- */
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const KebabIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <circle cx="12" cy="5" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);
const UserIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path
      d="M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const LinkIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path
      d="M10 14l-2 2a4 4 0 1 1-6-6l2-2m10 2l2-2a4 4 0 0 1 6 6l-2 2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const TrashIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path
      d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const CopyIcon = (p) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" {...p}>
    <rect x="9" y="9" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
    <rect x="5" y="3" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const CheckIcon = (p) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" {...p}>
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* --------------------------------- UI bits -------------------------------- */
const Pill = ({ children, className }) => (
  <span
    className={cx(
      'inline-flex items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 px-2.5 py-1 text-xs font-medium border border-slate-200 dark:border-slate-700',
      className
    )}
  >
    {children}
  </span>
);

const Section = ({ title, actions, children }) => (
  <section className="border-b border-slate-200/70 dark:border-slate-800 pb-6 mb-6 last:border-b-0 last:pb-0 last:mb-0">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-[11px] md:text-[11.5px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
        {title}
      </h3>
      {actions}
    </div>
    {children}
  </section>
);

/* -------------------------- Status select (allowed) ------------------------ */
// Minimal change: show only the current status text in the field.
// No "Current:" or "• Change…". Selecting a different option still triggers onChange.
function StatusSelect({ current, options = [], onChange, disabled, isLoading, helper }) {
  const pretty = (s) =>
    (s || '—')
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (m) => m.toUpperCase());

  const CURRENT_VALUE = '__current__';

  return (
    <div className="relative">
      <select
        value={CURRENT_VALUE}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== CURRENT_VALUE) onChange?.(next);
        }}
        disabled={disabled || isLoading || options.length === 0}
        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
      >
        <option value={CURRENT_VALUE} disabled>
          {pretty(current)}
        </option>
        {options.map((s) => (
          <option key={s} value={s}>
            {pretty(s)}
          </option>
        ))}
      </select>
      {isLoading ? (
        <div className="pointer-events-none absolute inset-y-0 right-2 inline-flex items-center text-slate-400">
          <svg className="size-4 animate-spin" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" />
          </svg>
        </div>
      ) : null}
      {helper}
    </div>
  );
}

/* ------------------------------ Timeline list ----------------------------- */
const StatusTimeline = React.memo(function StatusTimeline({ timeline = [] }) {
  if (!timeline.length) {
    return <div className="text-xs text-slate-500 dark:text-slate-400">No status changes yet.</div>;
  }
  const now = Date.now();
  const lastSix = timeline.slice(-6);
  return (
    <ol className="relative ml-2 border-l border-slate-200 dark:border-slate-800">
      {lastSix.map((sp, idx) => {
        const status = sp?.status
          ? sp.status.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
          : '—';
        const start = sp?.startedAt ? new Date(sp.startedAt).getTime() : null;
        const end = sp?.endedAt ? new Date(sp.endedAt).getTime() : null;
        const dur = start ? humanizeDuration((end ?? now) - start) : '';
        const who =
          sp?.actor?.name || (sp?.actor?.id ? `User ${String(sp.actor.id).slice(-6)}` : 'System');
        return (
          <li key={idx} className="mb-4 ml-4">
            <span className="absolute -left-1.5 mt-1.5 inline-flex size-3 rounded-full bg-slate-300 ring-4 ring-white dark:bg-slate-600 dark:ring-slate-950" />
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{status}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {who} • {sp?.startedAt ? fmtDateTime(sp.startedAt) : '—'}
              {dur ? ` • ${dur}` : ''}
            </div>
            {sp?.reason ? (
              <div className="mt-1 text-xs text-slate-700 dark:text-slate-300">{sp.reason}</div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
});

/* ---------------------- Assignee multi-select (search) --------------------- */
const AssigneeSelect = React.memo(function AssigneeSelect({
  valueIds = [],
  snapshots = [],
  onChange,
  disabled,
  placeholder = 'Search users…',
  role,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const { data: baseUsers = [], isLoading: loadingBase } = useUsers({
    includeInvited: true,
    includeInactive: false,
    limit: 50,
    role,
  });

  const searching = q.trim().length >= MIN_SEARCH_CHARS;
  const { data: searchedUsers = [], isFetching: loadingSearch } = useUserSearch(q, {
    limit: 20,
    includeInvited: true,
    includeInactive: false,
    role,
    enabled: searching,
  });

  const snapshotMap = useMemo(
    () =>
      new Map(
        (snapshots || []).map((s) => [
          String(s.id || s._id),
          {
            id: String(s.id || s._id),
            name: s.name || s.email || `User ${String(s.id || s._id).slice(-6)}`,
            avatarUrl: s.avatarUrl || null,
          },
        ])
      ),
    [snapshots]
  );

  const sourceUsers = searching ? searchedUsers : baseUsers;

  const options = useMemo(() => {
    const norm = (sourceUsers || []).map((u) => ({
      id: String(u.id),
      name: u.name || u.email || `User ${String(u.id).slice(-6)}`,
      avatarUrl: u.avatarUrl || null,
    }));

    valueIds.forEach((id) => {
      const sid = String(id);
      if (!norm.some((n) => n.id === sid)) {
        const s = snapshotMap.get(sid);
        norm.unshift({
          id: sid,
          name: s?.name || `User ${sid.slice(-6)}`,
          avatarUrl: s?.avatarUrl || null,
        });
      }
    });

    return norm;
  }, [sourceUsers, valueIds, snapshotMap]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (boxRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  const toggle = useCallback(
    (id) => {
      if (disabled) return;
      const s = new Set(valueIds.map(String));
      const k = String(id);
      if (s.has(k)) s.delete(k);
      else s.add(k);
      onChange?.(Array.from(s));
    },
    [valueIds, onChange, disabled]
  );

  const loading = loadingBase || loadingSearch;

  return (
    <div className="relative" ref={boxRef}>
      {/* Input first (44px) */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          placeholder={placeholder}
          className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
        />
        {loading && (
          <div className="pointer-events-none absolute inset-y-0 right-2 inline-flex items-center text-slate-400">
            <svg className="size-4 animate-spin" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
              <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
          ) : (
            options.map((u) => {
              const active = valueIds.map(String).includes(String(u.id));
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={cx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                    active
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-200'
                      : 'hover:bg-slate-50 text-slate-700 dark:hover:bg-slate-800 dark:text-slate-200'
                  )}
                >
                  {u.avatarUrl ? (
                    <img src={u.avatarUrl} alt="" className="size-5 rounded-full" />
                  ) : (
                    <UserIcon className="size-4 text-slate-400" />
                  )}
                  <span className="truncate">{u.name}</span>
                  {active && <span className="ml-auto text-xs">Selected</span>}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Chips below input (aligned layout) */}
      <div className="mt-2 flex flex-wrap gap-2">
        {valueIds.length === 0 ? (
          <span className="text-sm text-slate-500">No assignees.</span>
        ) : (
          valueIds.map((id) => {
            const sid = String(id);
            const u = options.find((x) => x.id === sid) || snapshotMap.get(sid);
            const label = u?.name || `User ${sid.slice(-6)}`;
            return (
              <span
                key={sid}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {u?.avatarUrl ? (
                  <img src={u.avatarUrl} alt="" className="size-5 rounded-full" />
                ) : (
                  <UserIcon className="size-4 text-slate-400" />
                )}
                <span className="truncate max-w-[160px]">{label}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="ml-0.5 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
                    onClick={() => toggle(sid)}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })
        )}
      </div>
    </div>
  );
});

/* ------------------------------ Core (named) ------------------------------ */
export function WorkDetailsCore({
  workId,
  initialWork,
  onClose,
  onUpdated,
  onDeleted,
  onNavigate,
  canEdit = true,
}) {
  const enabled = Boolean(workId);

  // Data (DTO)
  const { data: fetched, isLoading, isError } = useWork(workId, { enabled });
  const work = useMemo(() => fetched || initialWork || null, [fetched, initialWork]);

  // Mutations
  const { mutateAsync: updateWork } = useUpdateWork();
  const { mutateAsync: transitionWork, isPending: isTransitioning } = useTransitionWork();
  const { mutateAsync: deleteWork } = useDeleteWork();

  // Derived DTO
  const id = work?.id || '';
  const number = work?.workNumber || null;
  const softDeleted = !!work?.meta?.softDeleted;

  const clientId = work?.clientSnapshot?.id ? String(work.clientSnapshot.id) : '';
  const clientName = work?.clientSnapshot?.name || (clientId ? `Client ${clientId.slice(-6)}` : '');

  const assigneeIds = Array.isArray(work?.assignees) ? work.assignees.map(String) : [];
  const assigneeSnapshots = Array.isArray(work?.assigneeSnapshots) ? work.assigneeSnapshots : [];

  // 🔧 don't hard-code fallback timezone; null means "let backend use company default"
  const tz = work?.schedule?.timezone ?? null;
  const rrule = work?.schedule?.rrule;
  const scheduleStartISO = work?.schedule?.start || null;
  const scheduleEndISO = work?.schedule?.end || null;
  const scheduleAllDay = Boolean(work?.schedule?.allDay);

  // Local UI state
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  // Overview: quick Status + Assignees
  const [assigneeIdList, setAssigneeIdList] = useState(assigneeIds);
  const lastAssigneesSig = useRef((assigneeIds || []).map(String).sort().join(','));
  const [savingAssignees, setSavingAssignees] = useState(false);
  const [assigneesSavedTick, setAssigneesSavedTick] = useState(0);

  // Schedule form (auto-save)
  const [start, setStart] = useState(scheduleStartISO ? toLocalInput(scheduleStartISO) : '');
  const [end, setEnd] = useState(scheduleEndISO ? toLocalInput(scheduleEndISO) : '');
  const [allDay, setAllDay] = useState(scheduleAllDay);
  const lastScheduleSig = useRef(
    JSON.stringify({
      start: scheduleStartISO || null,
      end: scheduleEndISO || null,
      allDay: scheduleAllDay,
    })
  );
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleSavedTick, setScheduleSavedTick] = useState(0);

  // Status feedback
  const [statusMeta, setStatusMeta] = useState({ state: 'idle', at: 0 }); // idle|saving|saved|error

  // Sync locals when work changes
  useEffect(() => {
    setStart(scheduleStartISO ? toLocalInput(scheduleStartISO) : '');
    setEnd(scheduleEndISO ? toLocalInput(scheduleEndISO) : '');
    setAllDay(scheduleAllDay);
    setAssigneeIdList(assigneeIds);
    lastAssigneesSig.current = (assigneeIds || []).map(String).sort().join(',');
    lastScheduleSig.current = JSON.stringify({
      start: scheduleStartISO || null,
      end: scheduleEndISO || null,
      allDay: scheduleAllDay,
    });
    setErrorMsg('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [work?.id]);

  /* ------------------------------ navigation ------------------------------ */
  const openRoute = useCallback(
    (kind, payload) => {
      if (onNavigate) {
        onNavigate({ kind, ...payload });
        return;
      }
      const idS = payload?.id ? String(payload.id) : '';
      const workKey = payload?.workKey ? String(payload.workKey) : '';
      const map = {
        work: () => `/work/${encodeURIComponent(workKey || idS)}`,
        client: () => `/clients/${encodeURIComponent(idS)}`,
        quote: () => `/quotes/${encodeURIComponent(idS)}`,
        invoice: () => `/invoices/${encodeURIComponent(idS)}`,
      };
      const url = map[kind]?.();
      if (url) window.location.assign(url);
    },
    [onNavigate]
  );

  const openFullWork = useCallback(() => {
    const workKey = number || id;
    if (workKey) openRoute('work', { workKey });
  }, [openRoute, number, id]);

  const copyNumber = useCallback(async () => {
    try {
      if (!number) return;
      await navigator.clipboard.writeText(String(number));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  }, [number]);

  const setError = (e, fallback) =>
    setErrorMsg(e?.message || e?.response?.data?.message || fallback);

  /* ------------------------------ auto-save: status ------------------------------ */
  const onChangeStatus = useCallback(
    async (nextStatus) => {
      if (!id || !nextStatus || !canEdit || softDeleted) return;
      try {
        setStatusMeta({ state: 'saving', at: Date.now() });
        setErrorMsg('');
        const saved = await transitionWork({ id, status: nextStatus });
        onUpdated?.(saved);
        setStatusMeta({ state: 'saved', at: Date.now() });
        setTimeout(() => setStatusMeta({ state: 'idle', at: Date.now() }), 1200);
      } catch (e) {
        setStatusMeta({ state: 'error', at: Date.now() });
        setError(e, 'Status change failed.');
      }
    },
    [id, canEdit, softDeleted, transitionWork, onUpdated]
  );

  /* ------------------------------ auto-save: assignees ------------------------------ */
  const assigneesDirty = useMemo(() => {
    const a = (assigneeIdList || []).map(String).sort().join(',');
    return a !== lastAssigneesSig.current;
  }, [assigneeIdList]);

  useDebouncedEffect(
    () => {
      (async () => {
        if (!id || !canEdit || softDeleted) return;
        if (!assigneesDirty) return;
        try {
          setSavingAssignees(true);
          setErrorMsg('');
          const saved = await updateWork({ id, patch: { assignees: assigneeIdList.map(String) } });
          lastAssigneesSig.current = (assigneeIdList || []).map(String).sort().join(',');
          onUpdated?.(saved);
          setAssigneesSavedTick((t) => t + 1);
        } catch (e) {
          setError(e, 'Failed to update assignees.');
        } finally {
          setSavingAssignees(false);
        }
      })();
    },
    [assigneeIdList, assigneesDirty, id, canEdit, softDeleted],
    600
  );

  /* ------------------------------ auto-save: schedule ------------------------------ */
  const scheduleDirty = useMemo(() => {
    const s = start ? new Date(start).toISOString() : null;
    const e = end ? new Date(end).toISOString() : null;
    const sig = JSON.stringify({ start: s, end: e, allDay });
    return sig !== lastScheduleSig.current;
  }, [start, end, allDay]);

  useDebouncedEffect(
    () => {
      (async () => {
        if (!id || !canEdit || softDeleted) return;
        if (!scheduleDirty) return;

        const nextStart = start ? new Date(start) : null;
        const nextEnd = end ? new Date(end) : null;

        if (nextStart && nextEnd && nextEnd < nextStart) {
          setErrorMsg('End time cannot be before start time.');
          return;
        }

        try {
          setSavingSchedule(true);
          setErrorMsg('');

          const schedulePatch = {
            start: nextStart ? nextStart.toISOString() : null,
            end: nextEnd ? nextEnd.toISOString() : null,
            allDay,
          };

          // Only send timezone if BE already has one; otherwise let BE use company default
          if (tz) {
            schedulePatch.timezone = tz;
          }

          const patch = { schedule: schedulePatch };

          const saved = await updateWork({ id, patch });
          lastScheduleSig.current = JSON.stringify({
            start: patch.schedule.start,
            end: patch.schedule.end,
            allDay: patch.schedule.allDay,
          });
          onUpdated?.(saved);
          setScheduleSavedTick((t) => t + 1);
        } catch (e) {
          setError(e, 'Failed to update schedule.');
        } finally {
          setSavingSchedule(false);
        }
      })();
    },
    [start, end, allDay, scheduleDirty, id, canEdit, softDeleted, tz],
    700
  );

  /* ------------------------------- derived UI ----------------------------- */
  const currentSinceInfo = work?.currentStatusSince
    ? `In status since ${fmtDateTime(work.currentStatusSince)} (${humanizeDuration(
        Date.now() - new Date(work.currentStatusSince).getTime()
      )})`
    : null;

  // what to show in the header: actual tz or "Company default" when none stored
  const tzDisplay = tz || 'Company default';

  // Kebab
  const [kebabOpen, setKebabOpen] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const onSoftDelete = useCallback(async () => {
    if (!id) return;
    try {
      setErrorMsg('');
      await deleteWork(id);
      onDeleted?.(id);
      onClose?.();
    } catch (e) {
      setError(e, 'Failed to delete.');
    }
  }, [id, deleteWork, onDeleted, onClose]);

  /* -------------------------------- render -------------------------------- */
  return (
    <>
      {/* Sticky Header */}
      <div
        className="sticky top-0 z-[1100] flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white/95 px-6 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95"
        role="heading"
        aria-level={1}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Pill>{TYPE_LABEL[work?.type] || 'Work'}</Pill>
            <Pill>{work?.statusLabel || work?.status || '—'}</Pill>
            {softDeleted && (
              <span className="ml-1 rounded bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-900/30">
                Deleted
              </span>
            )}
          </div>

          <div className="mt-2 flex min-w-0 items-center gap-3">
            <h2
              className="truncate text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100"
              title={work?.title}
            >
              {work?.title || '—'}
            </h2>
            {number && (
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="text-sm font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                  onClick={openFullWork}
                  title="Open full work"
                >
                  {number}
                </button>
                <button
                  type="button"
                  onClick={copyNumber}
                  className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  title="Copy Work #"
                  aria-label="Copy Work Number"
                >
                  {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
                </button>
              </div>
            )}
          </div>

          {clientId ? (
            <div className="mt-1">
              <a
                href={`/clients/${encodeURIComponent(clientId)}`}
                className="text-base md:text-lg font-medium text-indigo-700 underline hover:no-underline dark:text-indigo-300"
              >
                {clientName}
              </a>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {/* View page */}
          <button
            type="button"
            onClick={openFullWork}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            title="Go to Work details page"
          >
            <LinkIcon className="size-4" />
            View page
          </button>

          {/* Solid red Delete */}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={!canEdit || softDeleted}
            className={cx(
              'inline-flex items-center rounded-lg px-3 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2',
              canEdit && !softDeleted
                ? 'bg-rose-600 text-white hover:bg-rose-500 focus-visible:ring-rose-500'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            )}
            title="Delete work"
          >
            Delete
          </button>

          {/* Kebab */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setKebabOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              aria-haspopup="menu"
              aria-expanded={kebabOpen}
              aria-label="More"
              title="More"
            >
              <KebabIcon className="size-5" />
            </button>
            {kebabOpen && (
              <div
                className="absolute right-0 z-[1150] mt-2 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-950"
                role="menu"
              >
                <button
                  type="button"
                  onClick={() => {
                    setKebabOpen(false);
                    setConfirmDelete(true);
                  }}
                  disabled={!canEdit || softDeleted}
                  className={cx(
                    'block w-full px-3 py-2 text-left text-sm',
                    canEdit && !softDeleted
                      ? 'text-rose-600 hover:bg-rose-50'
                      : 'cursor-not-allowed text-slate-400'
                  )}
                  role="menuitem"
                >
                  <span className="inline-flex items-center gap-2">
                    <TrashIcon className="size-4" />
                    Delete
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Close */}
          {onClose ? (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <XIcon className="size-5" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Error banner */}
      {errorMsg ? (
        <div className="border-b border-rose-200 bg-rose-50 px-6 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200">
          {errorMsg}
        </div>
      ) : null}

      {/* Body */}
      <div
        className="flex-1 overflow-y-auto px-6 py-6"
        style={{ paddingBottom: 'calc(var(--dock-bottom, 0px) + 16px)' }}
      >
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-7 w-1/3 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="h-24 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="h-24 rounded bg-slate-200 dark:bg-slate-800" />
          </div>
        ) : isError || !work ? (
          <div className="text-sm text-rose-600 dark:text-rose-400">Failed to load details.</div>
        ) : (
          <>
            {/* Quick controls row */}
            <Section title="Quick actions">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Status (auto-save) */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Current status
                  </label>
                  <StatusSelect
                    current={work?.status}
                    options={Array.isArray(work?.allowedNextStatuses) ? work.allowedNextStatuses : []}
                    onChange={onChangeStatus}
                    disabled={!canEdit || softDeleted}
                    isLoading={isTransitioning || statusMeta.state === 'saving'}
                    helper={
                      <div className="mt-1 text-xs text-slate-500">
                        {statusMeta.state === 'saving'
                          ? 'Saving…'
                          : statusMeta.state === 'saved'
                          ? 'Saved'
                          : currentSinceInfo || ''}
                      </div>
                    }
                  />
                </div>

                {/* Assignees (auto-save) */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Assignees
                  </label>
                  <AssigneeSelect
                    valueIds={assigneeIdList}
                    snapshots={assigneeSnapshots}
                    onChange={setAssigneeIdList}
                    disabled={!canEdit || softDeleted}
                  />
                  <div className="mt-1 text-xs text-slate-500">
                    {savingAssignees ? 'Saving…' : assigneesSavedTick ? 'Saved' : 'Autosave is on'}
                  </div>
                </div>
              </div>
            </Section>

            {/* Schedule (auto-save) */}
            <Section
              title="Schedule"
              actions={
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {tzDisplay}
                  {rrule ? ' • Repeats' : ''}
                </span>
              }
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Start
                  </label>
                  <input
                    type="datetime-local"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    disabled={!canEdit || softDeleted}
                    className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    End
                  </label>
                  <input
                    type="datetime-local"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    disabled={!canEdit || softDeleted}
                    className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
              <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                  disabled={!canEdit || softDeleted}
                  className="size-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-60"
                />
                All day
              </label>
              <div className="mt-1 text-xs text-slate-500">
                {savingSchedule ? 'Saving…' : scheduleSavedTick ? 'Saved' : 'Autosave is on'}
              </div>
            </Section>

            {/* Status history */}
            <Section title="Status history">
              <StatusTimeline timeline={Array.isArray(work?.statusTimeline) ? work.statusTimeline : []} />
              <div className="mt-2 text-xs text-slate-500">
                For full history, open the Work page Timeline.
              </div>
            </Section>

            {/* Meta */}
            <div className="mt-6 text-xs text-slate-500 dark:text-slate-400">
              Created {fmtDateTime(work?.meta?.createdAt)} · Updated {fmtDateTime(work?.meta?.updatedAt)}
            </div>
          </>
        )}
      </div>

      {/* Confirm delete */}
      {confirmDelete ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDelete(false)} />
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-950">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Delete work?</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              This is a soft delete. Admins can restore it later.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSoftDelete}
                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

WorkDetailsCore.propTypes = {
  workId: PropTypes.string,
  initialWork: PropTypes.any,
  onClose: PropTypes.func,
  onUpdated: PropTypes.func,
  onDeleted: PropTypes.func,
  onNavigate: PropTypes.func, // ({ kind, id?, workKey? })
  canEdit: PropTypes.bool,
};

/* ------------------------------ Drawer wrapper ---------------------------- */
export default function WorkDetailsDrawer({
  open,
  workId,
  initialWork,
  onClose,
  onUpdated,
  onDeleted,
  onNavigate,
  canEdit = true,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Work details drawer">
      {/* Overlay */}
      <div className="flex-1 bg-black/30 backdrop-blur-[1px]" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <aside className="relative w-full max-w-3xl bg-white dark:bg-slate-950 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 flex flex-col">
        <WorkDetailsCore
          workId={workId}
          initialWork={initialWork}
          onClose={onClose}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
          onNavigate={onNavigate}
          canEdit={canEdit}
        />
      </aside>
    </div>
  );
}

WorkDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  workId: PropTypes.string,
  initialWork: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  onDeleted: PropTypes.func,
  onNavigate: PropTypes.func, // ({ kind, id?, workKey? })
  canEdit: PropTypes.bool,
};
